import React, { useEffect } from 'react';
import { useTheme } from '../ThemeContext';
import { TOUR_CARD_STYLE, pickTourNode, tourTargetSelector } from '../shared/guidedTour';

const GuidedTourCard = ({ step, index, total, onNext, onBack, onSkip }) => {
  const { colors } = useTheme();
  const last = index >= total - 1;

  useEffect(() => {
    if (!step || !step.target || typeof document === 'undefined') return undefined;
    const selector = tourTargetSelector(step.target);
    let node = null;
    const apply = () => {
      const next = pickTourNode(document.querySelectorAll(selector));
      if (next === node) return;
      if (node) node.classList.remove('guided-tour-target');
      node = next;
      if (!node) return;
      node.classList.add('guided-tour-target');
      if (typeof node.scrollIntoView === 'function') {
        node.scrollIntoView({ block: 'nearest', inline: 'nearest' });
      }
    };
    const frame = window.requestAnimationFrame(apply);
    const observer = typeof MutationObserver === 'function'
      ? new MutationObserver(() => apply())
      : null;
    if (observer && document.body) {
      observer.observe(document.body, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['disabled', 'data-tour'],
      });
    }
    return () => {
      window.cancelAnimationFrame(frame);
      if (observer) observer.disconnect();
      if (node) node.classList.remove('guided-tour-target');
      document.querySelectorAll('.guided-tour-target').forEach((item) => {
        if (item.getAttribute('data-tour') === step.target) item.classList.remove('guided-tour-target');
      });
    };
  }, [step]);

  if (!step) return null;

  const button = {
    flex: '1 1 5.5rem',
    minWidth: 0,
    minHeight: '44px',
    padding: '0.55rem 0.7rem',
    borderRadius: '0.5rem',
    fontWeight: 700,
    fontSize: '0.9rem',
    cursor: 'pointer',
  };

  return (
    <>
      <div
        className="guided-tour-layer"
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0, 0, 0, 0.55)',
          zIndex: 10000,
        }}
      />
      <div
        className="guided-tour-card"
        role="dialog"
        aria-label={step.title}
        onClick={(event) => event.stopPropagation()}
        style={{
          ...TOUR_CARD_STYLE,
          background: colors.surface,
          border: `1px solid ${colors.border}`,
          boxShadow: '0 12px 40px rgba(0, 0, 0, 0.28)',
        }}
      >
        <div style={{ overflowY: 'auto', minHeight: 0 }}>
          <p style={{ margin: '0 0 0.35rem', color: colors.textSecondary, fontSize: '0.8rem' }}>
            Step {index + 1} of {total}
          </p>
          <h2 style={{ margin: '0 0 0.4rem', color: colors.text, fontSize: '1.05rem', lineHeight: 1.3 }}>{step.title}</h2>
          <p style={{ margin: 0, color: colors.textSecondary, fontSize: '0.95rem', lineHeight: 1.45 }}>{step.text}</p>
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem', marginTop: '0.85rem', flexShrink: 0 }}>
          <button type="button" onClick={onSkip} style={{ ...button, background: colors.surfaceAlt, color: colors.text, border: `1px solid ${colors.border}` }}>
            Skip
          </button>
          {index > 0 && (
            <button type="button" onClick={onBack} style={{ ...button, background: colors.surfaceAlt, color: colors.text, border: `1px solid ${colors.border}` }}>
              Previous
            </button>
          )}
          <button
            type="button"
            onClick={onNext}
            style={{ ...button, background: 'linear-gradient(135deg, #59c4c4 0%, #4ab3b3 100%)', color: 'white', border: 'none' }}
          >
            {last ? 'Done' : 'Next'}
          </button>
        </div>
      </div>
    </>
  );
};

export default GuidedTourCard;
